import { Container } from "pixi.js";
import type { PresentationTaskRunner, SpriteText } from "@slot/presentation";
import type { LocalizedText } from "@slot/game-shell";

/**
 * WinMeterView — UI-only: the plank above the grid, with two faces. The total in its sprite text (nothing when the
 * font did not load), or a one-line tip while there is none; the scene parents the tip's line to the grid, so it moves
 * with the plank art. The presenter formats the total and picks the tips; the view answers for its font:
 * `amountOmitsCurrencySymbol` (the sprite font has no currency glyph). The pulse and the tip's fades ride the CHROME
 * runner: they pause and frame-step with the game but never speed-scale.
 */
export class WinMeterView extends Container {
  private readonly spriteText: SpriteText | null;
  private readonly tip: LocalizedText | null;
  private readonly chromeRunner: PresentationTaskRunner;
  private restScale: { x: number; y: number } | null = null;
  private pulseGeneration = 0;
  /** Bumped by each tip fade; the one it replaces stops writing the alpha. */
  private tipGeneration = 0;

  public constructor(input: { spriteText: SpriteText | null; tip: LocalizedText | null; chromeRunner: PresentationTaskRunner }) {
    super({ label: "WinMeterPanel" });
    this.spriteText = input.spriteText;
    this.tip = input.tip;
    this.chromeRunner = input.chromeRunner;
    if (this.tip) {
      this.tip.alpha = 0;
      this.tip.visible = false;
    }
  }

  /** Sets the displayed text; a meter built without its sprite text shows nothing. */
  public setText(text: string): void {
    this.spriteText?.setText(text);
  }

  /** True when the text renders with the sprite font, which has no currency glyph (the presenter formats without it). */
  public get amountOmitsCurrencySymbol(): boolean {
    return this.spriteText !== null;
  }

  /** Where a tumble's win lands as it flies in (the composition root hands it to the win highlight); null without the font. */
  public get collectTarget(): Container | null {
    return this.spriteText;
  }

  private get pulseTarget(): Container {
    return this.spriteText ?? this;
  }

  /** Quick pulse to emphasize an update. */
  public async pulse(durationSeconds: number = 0.16, scaleUp: number = 1.1): Promise<void> {
    const target = this.pulseTarget;
    const gen = ++this.pulseGeneration;
    if (!this.restScale) this.restScale = { x: target.scale.x, y: target.scale.y };
    const base = this.restScale;
    target.scale.set(base.x, base.y);
    const up = { x: base.x * scaleUp, y: base.y * scaleUp };
    const half = Math.max(0.001, durationSeconds * 0.5);
    await this.chromeRunner.tweenProgress(half, (t) => {
      if (gen === this.pulseGeneration) target.scale.set(base.x + (up.x - base.x) * t, base.y + (up.y - base.y) * t);
    }, undefined, { easing: "quadOut" });
    if (gen !== this.pulseGeneration) return;
    await this.chromeRunner.tweenProgress(half, (t) => {
      if (gen === this.pulseGeneration) target.scale.set(up.x + (base.x - up.x) * t, up.y + (base.y - up.y) * t);
    }, undefined, { easing: "quadIn" });
    if (gen === this.pulseGeneration) target.scale.set(base.x, base.y);
  }

  /** The total's face. */
  public show(): void {
    if (this.spriteText) this.spriteText.visible = true;
  }

  public hide(): void {
    if (this.spriteText) this.spriteText.visible = false;
  }

  /** The tip's face shows `key`'s line: the tip up fades out, then the new one in, each over `fadeSeconds`. */
  public async showTip(key: string, fadeSeconds: number): Promise<void> {
    const tip = this.tip;
    if (!tip) return;
    const gen = ++this.tipGeneration;
    if (tip.visible) await this.fadeTip(tip, 0, fadeSeconds, gen);
    if (gen !== this.tipGeneration) return;
    tip.setKey(key);
    tip.visible = true;
    await this.fadeTip(tip, 1, fadeSeconds, gen);
  }

  /** The tip's face fades out over `fadeSeconds`. */
  public async hideTip(fadeSeconds: number): Promise<void> {
    const tip = this.tip;
    if (!tip || !tip.visible) return;
    const gen = ++this.tipGeneration;
    await this.fadeTip(tip, 0, fadeSeconds, gen);
    if (gen === this.tipGeneration) tip.visible = false;
  }

  private fadeTip(tip: LocalizedText, to: number, seconds: number, gen: number): Promise<void> {
    const from = tip.alpha;
    return this.chromeRunner.tweenProgress(seconds, (k) => {
      if (gen === this.tipGeneration) tip.alpha = from + (to - from) * k;
    }, undefined, { easing: "linear" });
  }
}
